import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { kpiCatalog } from "@/api";
import { PILLARS } from "@/constants";
import { useAuth } from "@/auth";
import { EquivalenceTable } from "@/components/EquivalenceTable";
import { AlertTriangle, ArrowLeft, BookOpen, Plus, Pencil, Trash2, RotateCcw, Save, X, ChevronDown, ChevronRight, Upload, FlaskConical, Ruler, Grid3x3, Lightbulb } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
import CategoryMultiSelect from "@/components/CategoryMultiSelect";
import Modal from "@/components/Modal";

const PILLAR_COLOR = {
  calidad: "#0033AD",
  costo: "#E0162B",
  continuidad: "#0033AD",
  innovacion: "#FFC400",
  sustentabilidad: "#7BAE3F",
  relacion: "#11142B",
};

// Categorías de un KPI: `categories` (lista) o, en KPIs previos, `category` (texto único).
const kpiCategories = (k) => (k.categories?.length ? k.categories : [k.category || "Todas"]);

// "Por defecto" es el rol de una rúbrica fallback, no una categoría más: se activa con su propia casilla.
const DEFAULT_CATEGORY = "Por defecto";

const EMPTY = {
  pillar: "continuidad",
  category: "Todas",
  categories: ["Todas"],
  fallback: false,
  criterion_code: "",
  criterion_label: "",
  subcriterion: "",
  indicator_code: "",
  indicator_label: "",
  description: "",
  evaluation_instrument: "",
  scoring_method: "",
  periodicity: "mensual",
  levels: [
    { score: 1, label: "" },
    { score: 2, label: "" },
    { score: 3, label: "" },
    { score: 4, label: "" },
  ],
  weight: 1.0,
  order: 0,
  active: true,
  is_proposed: false,
};

export default function KpiCatalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);  // null | {} (new) | row (edit)
  const [deleting, setDeleting] = useState(null);  // null | { row, impact } pendiente de confirmar
  const [expanded, setExpanded] = useState({});  // pillar+criterion → bool
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const load = async (inactive = showInactive) => {
    try {
      const res = await kpiCatalog.list({ active_only: !inactive });
      setData(res);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(showInactive); }, [showInactive]);

  const totals = useMemo(() => {
    if (!data) return { total: 0, byPillar: {} };
    const byPillar = {};
    for (const p of data.pillars || []) {
      byPillar[p.pillar] = p.total_indicators;
    }
    return { total: data.total || 0, byPillar };
  }, [data]);

  const toggleExpand = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const onSave = async (row) => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = {
        pillar: row.pillar,
        category: kpiCategories(row)[0],
        categories: kpiCategories(row),
        criterion_code: row.criterion_code,
        criterion_label: row.criterion_label,
        subcriterion: row.subcriterion || "",
        indicator_code: row.indicator_code,
        indicator_label: row.indicator_label,
        description: row.description || "",
        evaluation_instrument: row.evaluation_instrument || "",
        scoring_method: row.scoring_method || "",
        periodicity: row.periodicity || "mensual",
        levels: (row.levels || []).filter((l) => l.label.trim()),
        weight: Number(row.weight || 1),
        order: Number(row.order || 0),
        active: !!row.active,
        is_proposed: !!row.is_proposed,
        fallback: !!row.fallback,
      };
      if (row.id) {
        await kpiCatalog.update(row.id, payload);
      } else {
        await kpiCatalog.create(payload);
      }
      setMsg("Indicador guardado.");
      setTimeout(() => setMsg(""), 2500);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const doDelete = async (row) => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await kpiCatalog.remove(row.id);
      setDeleting(null);
      setMsg("Indicador eliminado.");
      setTimeout(() => setMsg(""), 2500);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Siempre se confirma en el modal; si eliminar el KPI afecta capturas o la cobertura de pilares, se advierte ahí.
  const onDelete = async (row) => {
    setMsg(""); setErr("");
    let impact;
    try {
      impact = await kpiCatalog.deleteImpact(row.id);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      return;
    }
    setDeleting({ row, impact });
  };

  const onResetDefaults = async () => {
    if (!window.confirm("Esto borra el catálogo actual y carga los KPIs oficiales del Excel LALA 2026-2027. ¿Continuar?")) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      const res = await kpiCatalog.resetDefaults();
      setMsg(`Catálogo restaurado · ${res.inserted} indicadores cargados.`);
      setTimeout(() => setMsg(""), 4000);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!data) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando catálogo de KPIs…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="kpi-catalog-page">
      {isAdmin && (
      <Link to="/admin/configuracion" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <BookOpen size={18} /> catálogo de evaluación
          </div>
          <h1 className="font-display text-4xl">Catálogo de KPIs</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
            Estructura jerárquica <strong>Pilar → Criterio → Indicador</strong> con rúbrica de <strong>4 niveles cualitativos</strong>.
            Es la base transparente con la que Lala acompaña a sus proveedores — cada indicador y cada nivel a la vista,
            porque <em>somos auténticos</em> y <em>no tomamos atajos</em>.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
          <>
          <Link to="/admin/matriz-kpis" className="sketch-pill bg-[var(--lala-blue)] text-white px-3 py-2 text-sm font-semibold inline-flex items-center gap-2" data-testid="matrix-link">
            <Grid3x3 size={14} /> Matriz de KPIs por proveedor
          </Link>
          <ExcelImportButton onAfterImport={load} setMsg={setMsg} setErr={setErr} />
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary text-sm" data-testid="add-kpi-btn">
            <Plus size={14} /> Nuevo indicador
          </button>
          <button onClick={onResetDefaults} disabled={busy} className="sketch-pill bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)] px-3 py-2 text-sm font-semibold inline-flex items-center gap-2" data-testid="reset-defaults-btn">
            <RotateCcw size={14} /> Cargar KPIs oficiales LALA 2026
          </button>
          <button onClick={() => setShowInactive(!showInactive)} className="sketch-pill bg-[var(--cream-deep)] text-[var(--ink-soft)] px-3 py-2 text-sm font-semibold" data-testid="toggle-inactive-btn">
            {showInactive ? "Ocultar inactivos" : "Ver inactivos (histórico)"}
          </button>
          </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <EquivalenceTable />
      </div>

      {/* Resumen */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="sketch-border bg-[var(--cream-soft)] p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Total indicadores</div>
          <div className="font-display text-3xl text-[var(--lala-blue)]" data-testid="kpi-total">{totals.total}</div>
        </div>
        {PILLARS.filter((p) => totals.byPillar[p.key]).map((p) => (
          <div key={p.key} className="sketch-border bg-[var(--cream-soft)] p-4" data-testid={`kpi-pillar-count-${p.key}`}>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">{p.label}</div>
            <div className="font-display text-3xl" style={{ color: PILLAR_COLOR[p.key] }}>{totals.byPillar[p.key] || 0}</div>
            <div className="text-[11px] text-[var(--ink-soft)] mt-1">indicadores</div>
          </div>
        ))}
      </div>

      {/* Banner explicativo propuesta vs oficial */}
      {(data.raw_items || []).some(r => r.is_proposed) && (
        <div className="mb-6 bg-[#FFFBEA] border border-[#FFC400] rounded-xl p-4 flex items-start gap-3" data-testid="proposed-info-banner">
          <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FFC400] text-[var(--ink)]">
            <Lightbulb size={14} />
          </span>
          <div className="flex-1 text-sm">
            <div className="font-display text-base mb-1">Indicadores marcados como <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FFC400] text-[var(--ink)]">PROPUESTA</span></div>
            <p className="text-[var(--ink-soft)] leading-snug">
              Los indicadores marcados como <strong>propuesta</strong> no forman parte del catálogo oficial vigente de Lala. Permanecen separados para revisión y sólo deben promoverse a Oficial cuando Lala los apruebe expresamente.
            </p>
          </div>
        </div>
      )}

      {msg && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="kpi-msg">{msg}</div>}
      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="kpi-err">{err}</div>}

      {/* Tree */}
      <div className="space-y-4">
        {(data.pillars || []).map((p) => (
          <section key={p.pillar} className="sketch-border bg-[var(--cream-soft)] p-5" data-testid={`kpi-pillar-${p.pillar}`}>
            <header className="flex items-baseline justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: PILLAR_COLOR[p.pillar] }}>
                  Pilar · {p.pillar_label}
                </div>
                <h2 className="font-display text-2xl mt-0.5">{p.pillar_label}</h2>
              </div>
              <div className="text-xs text-[var(--ink-soft)]">{p.total_indicators} indicadores</div>
            </header>

            <div className="space-y-2">
              {(p.criteria || []).map((c) => {
                const key = `${p.pillar}::${c.criterion_code}`;
                const open = expanded[key] ?? true;
                return (
                  <div key={key} className="bg-white border border-[var(--line)] rounded-xl overflow-hidden" data-testid={`kpi-criterion-${c.criterion_code}`}>
                    <button onClick={() => toggleExpand(key)} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--cream-soft)] transition-colors">
                      <div className="flex items-center gap-2 text-left">
                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">{c.criterion_code}</div>
                          <div className="font-display text-lg">{c.criterion_label}</div>
                        </div>
                      </div>
                      <div className="text-xs text-[var(--ink-soft)]">{c.indicators.length} indicadores</div>
                    </button>
                    {open && (
                      <div className="divide-y divide-[var(--line)] border-t border-[var(--line)]">
                        {c.indicators.map((ind) => (
                          <article key={ind.id} className="px-4 py-3" data-testid={`kpi-indicator-${ind.indicator_code}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold flex items-center gap-2 flex-wrap">
                                  <span>{ind.indicator_code} · {ind.subcriterion || ind.criterion_label} · {kpiCategories(ind).join(", ")}</span>
                                  {ind.fallback && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[var(--cream-deep)] text-[var(--ink)]" data-testid={`fallback-badge-${ind.indicator_code}`} title="Rúbrica por defecto: solo se usa en los pilares donde la categoría del proveedor no tiene KPIs propios">
                                      POR DEFECTO
                                    </span>
                                  )}
                                  {ind.is_proposed && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[#FFC400] text-[var(--ink)] flex items-center gap-1" data-testid={`proposed-badge-${ind.indicator_code}`} title="Propuesta del equipo LALA Elite — pendiente de validación oficial Lala">
                                      <Lightbulb size={9} /> PROPUESTA
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-display text-base mt-0.5">{ind.indicator_label}</h3>
                                {ind.description && <p className="text-xs text-[var(--ink-soft)] mt-1">{ind.description}</p>}
                                {(ind.evaluation_instrument || ind.scoring_method) && (
                                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                                    {ind.evaluation_instrument && (
                                      <div className="bg-[var(--cream-soft)] border border-[var(--line)] rounded p-2">
                                        <div className="text-[9px] uppercase tracking-wider text-[var(--lala-blue)] font-bold flex items-center gap-1 mb-0.5">
                                          <FlaskConical size={10} /> Instrumento de evaluación
                                        </div>
                                        <div className="text-[11px] text-[var(--ink)] leading-snug">{ind.evaluation_instrument}</div>
                                      </div>
                                    )}
                                    {ind.scoring_method && (
                                      <div className="bg-[var(--cream-soft)] border border-[var(--line)] rounded p-2">
                                        <div className="text-[9px] uppercase tracking-wider text-[var(--lala-blue)] font-bold flex items-center gap-1 mb-0.5">
                                          <Ruler size={10} /> Criterio de calificación
                                        </div>
                                        <div className="text-[11px] text-[var(--ink)] leading-snug">{ind.scoring_method}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <ul className="mt-2 grid sm:grid-cols-2 gap-1.5">
                                  {(ind.levels || []).map((l) => (
                                    <li key={l.score} className="text-[11px] text-[var(--ink)] bg-[var(--cream-soft)] border border-[var(--line)] rounded px-2 py-1 leading-snug">
                                      <span className="inline-block w-5 h-5 rounded-full bg-[var(--lala-blue)] text-white text-[10px] font-bold mr-1.5 text-center leading-5">{l.score}</span>
                                      {l.label}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {isAdmin && (
                              <div className="flex gap-1.5 shrink-0">
                                <button onClick={() => setEditing({ ...ind })} className="p-1.5 rounded hover:bg-[var(--cream-soft)] text-[var(--lala-blue)]" data-testid={`edit-kpi-${ind.indicator_code}`} title="Editar">
                                  <Pencil size={14} />
                                </button>
                                <button onClick={() => onDelete(ind)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-kpi-${ind.indicator_code}`} title="Eliminar">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              )}
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {!(data.pillars || []).length && (
          <div className="text-center py-16 sketch-border bg-[var(--cream-soft)]" data-testid="kpi-empty">
            <BookOpen size={36} className="mx-auto text-[var(--lala-blue)] opacity-50" />
            <p className="mt-3 text-sm text-[var(--ink-soft)]">El catálogo está vacío. Carga los KPIs oficiales LALA 2026 o crea el primero.</p>
            <button onClick={onResetDefaults} className="btn-primary mt-4 text-sm">
              <RotateCcw size={14} /> Cargar KPIs oficiales
            </button>
          </div>
        )}
      </div>

      {deleting && (
        <DeleteKpiModal row={deleting.row} impact={deleting.impact} busy={busy}
          onConfirm={() => doDelete(deleting.row)} onClose={() => setDeleting(null)} />
      )}

      {editing && (
        <KpiEditor
          row={editing}
          onSave={onSave}
          onClose={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

const pillarLabel = (key) => PILLARS.find((p) => p.key === key)?.label || key;

function DeleteKpiModal({ row, impact, busy, onConfirm, onClose }) {
  const lostPillars = (impact.affected_pillars || []).map(pillarLabel).join(", ");
  return (
    <Modal testId="kpi-delete-modal" align="center" onClose={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl" role="alertdialog" aria-labelledby="kpi-delete-title">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="shrink-0 text-[var(--lala-red,#E0162B)] mt-0.5" />
          <div>
            <h2 id="kpi-delete-title" className="font-display text-xl">¿Eliminar {row.indicator_code}?</h2>
            <p className="text-sm text-[var(--ink-soft)] mt-0.5">{row.indicator_label}</p>
          </div>
        </div>
        {!impact.has_impact && (
          <p className="mt-4 text-sm" data-testid="kpi-delete-plain">
            Se eliminará del catálogo. Esta acción no se puede deshacer.
          </p>
        )}
        <ul className="mt-4 space-y-3 text-sm" data-testid="kpi-delete-warnings">
          {impact.fallback && (
            <li>Es una <b>rúbrica por defecto</b>: las categorías sin KPIs propios en su pilar dependen de ella para poder evaluarse.</li>
          )}
          {impact.captures_count > 0 && (
            <li data-testid="kpi-delete-captures">
              Tiene <b>{impact.captures_count} {impact.captures_count === 1 ? "captura" : "capturas"}</b> de {impact.captures_suppliers} {impact.captures_suppliers === 1 ? "proveedor" : "proveedores"}.
              Seguirán guardadas, pero dejarán de contar en puntajes y reportes. Si vuelves a crear el KPI con el mismo código se recuperan.
            </li>
          )}
          {impact.affected_suppliers_count > 0 && (
            <li data-testid="kpi-delete-pillars">
              <b>{impact.affected_suppliers_count} {impact.affected_suppliers_count === 1 ? "proveedor se quedaría" : "proveedores se quedarían"} sin KPIs en: {lostPillars}.</b>{" "}
              No podrán evaluarse en {impact.affected_pillars.length === 1 ? "ese pilar" : "esos pilares"} hasta que exista otro KPI, y el resultado publicado del mes en curso pasará a borrador al guardar su siguiente captura.
              <div className="mt-1 text-xs text-[var(--ink-soft)]">
                {impact.affected_suppliers.map((s) => s.name).join(", ")}
                {impact.affected_suppliers_count > impact.affected_suppliers.length && ` y ${impact.affected_suppliers_count - impact.affected_suppliers.length} más`}
              </div>
            </li>
          )}
        </ul>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="sketch-pill bg-white border border-[var(--ink-soft)] text-[var(--ink)] px-4 py-2 text-sm" data-testid="kpi-delete-cancel">Cancelar</button>
          <button onClick={onConfirm} disabled={busy} className="sketch-pill bg-red-600 text-white px-4 py-2 text-sm font-semibold" data-testid="kpi-delete-confirm">
            {busy ? "Eliminando…" : impact.has_impact ? "Eliminar de todos modos" : "Eliminar"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function KpiEditor({ row, onSave, onClose, busy }) {
  const cats = useCategories();
  const [model, setModel] = useState(row);
  const selectedCats = kpiCategories(model);
  const isDefault = !!model.fallback && selectedCats.includes(DEFAULT_CATEGORY);
  const notDefault = (c) => c !== DEFAULT_CATEGORY;
  // "Todas" siempre se ofrece; "Por defecto" no es una opción de categoría (ver casilla de rúbrica por defecto).
  const CATEGORIES = ["Todas", ...new Set([...cats.filter((c) => c !== "Todas"), ...selectedCats.filter((c) => c !== "Todas")].filter(notDefault))];
  // Activar la rúbrica por defecto la deja solo en "Por defecto"; desactivarla vuelve a "Todas".
  const toggleDefault = () => setModel((p) => {
    const on = !(p.fallback && kpiCategories(p).includes(DEFAULT_CATEGORY));
    const next = on ? [DEFAULT_CATEGORY] : ["Todas"];
    return { ...p, fallback: on, categories: next, category: next[0] };
  });
  // "Todas" excluye a las demás; si se desmarca la última categoría, vuelve a "Todas".
  const toggleCategory = (c) => setModel((p) => {
    const cur = kpiCategories(p);
    let next;
    if (c === "Todas") next = ["Todas"];
    else {
      const base = cur.filter((x) => x !== "Todas");
      next = base.includes(c) ? base.filter((x) => x !== c) : [...base, c];
    }
    if (!next.length) next = ["Todas"];
    return { ...p, categories: next, category: next[0] };
  });
  const setField = (k, v) => setModel((p) => ({ ...p, [k]: v }));
  const setLevel = (i, v) => setModel((p) => {
    const levels = [...(p.levels || [])];
    levels[i] = { ...levels[i], label: v };
    return { ...p, levels };
  });
  // Make sure 4 levels exist
  const levels = useMemo(() => {
    const arr = [...(model.levels || [])];
    while (arr.length < 4) arr.push({ score: arr.length + 1, label: "" });
    return arr.slice(0, 4);
  }, [model.levels]);

  return (
    <Modal testId="kpi-editor-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">{model.id ? "Editar indicador" : "Nuevo indicador"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-[var(--cream-soft)] rounded" data-testid="kpi-editor-close"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Pilar">
            <select value={model.pillar} onChange={(e) => setField("pillar", e.target.value)} className="input-field" data-testid="kpi-editor-pillar">
              {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </Field>
          <div className="block">
            <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Categorías de compra (una o más)</div>
            <CategoryMultiSelect options={CATEGORIES} selected={selectedCats} onToggle={toggleCategory} disabled={isDefault} />
            <label className="flex items-start gap-2 mt-2 text-xs text-[var(--ink-soft)] cursor-pointer">
              <input type="checkbox" checked={isDefault} onChange={toggleDefault}
                className="accent-[var(--lala-blue)] w-4 h-4 mt-0.5" data-testid="kpi-editor-default-toggle" />
              <span>Rúbrica por defecto: solo se usa en los pilares donde la categoría del proveedor no tiene KPIs propios.</span>
            </label>
          </div>
          <Field label="Periodicidad de captura">
            <select value={model.periodicity || "mensual"} onChange={(e) => setField("periodicity", e.target.value)} className="input-field" data-testid="kpi-editor-periodicity">
              <option value="mensual">Mensual (todos los meses)</option>
              <option value="bimestral">Bimestral (Feb/Abr/Jun/Ago/Oct/Dic)</option>
              <option value="trimestral">Trimestral (Mar/Jun/Sep/Dic)</option>
              <option value="semestral">Semestral (Mar/Sep)</option>
              <option value="anual">Anual (Dic)</option>
            </select>
          </Field>
          <Field label="Código del criterio (e.g. 1.1)">
            <input type="text" value={model.criterion_code} onChange={(e) => setField("criterion_code", e.target.value)} className="input-field" data-testid="kpi-editor-criterion-code" />
          </Field>
          <Field label="Nombre del criterio">
            <input type="text" value={model.criterion_label} onChange={(e) => setField("criterion_label", e.target.value)} className="input-field" data-testid="kpi-editor-criterion-label" />
          </Field>
          <Field label="Subcriterio (opcional)">
            <input type="text" value={model.subcriterion || ""} onChange={(e) => setField("subcriterion", e.target.value)} className="input-field" />
          </Field>
          <Field label="Código del indicador (e.g. 1.1.1)">
            <input type="text" value={model.indicator_code} onChange={(e) => setField("indicator_code", e.target.value)} className="input-field" data-testid="kpi-editor-indicator-code" />
          </Field>
          <Field label="Nombre del indicador" full>
            <input type="text" value={model.indicator_label} onChange={(e) => setField("indicator_label", e.target.value)} className="input-field" data-testid="kpi-editor-indicator-label" />
          </Field>
          <Field label="Descripción / propósito del indicador" full>
            <textarea value={model.description || ""} onChange={(e) => setField("description", e.target.value)} rows={2} className="input-field" />
          </Field>
          <Field label="Instrumento de Evaluación" full>
            <textarea value={model.evaluation_instrument || ""} onChange={(e) => setField("evaluation_instrument", e.target.value)} rows={2} className="input-field" placeholder="¿Con qué herramienta o proceso se mide este indicador?" data-testid="kpi-editor-instrument" />
          </Field>
          <Field label="Criterio de Calificación" full>
            <textarea value={model.scoring_method || ""} onChange={(e) => setField("scoring_method", e.target.value)} rows={2} className="input-field" placeholder="¿Cómo se calcula la nota 1-4? (cualitativo, cuantitativo, etc.)" data-testid="kpi-editor-method" />
          </Field>
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold mb-2">Rúbrica · 4 niveles (1 más bajo · 4 más alto)</div>
          <div className="space-y-2">
            {levels.map((l, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--lala-blue)] text-white font-bold text-sm leading-7 text-center">{l.score}</span>
                <textarea
                  value={l.label}
                  onChange={(e) => setLevel(i, e.target.value)}
                  rows={2}
                  className="input-field flex-1"
                  placeholder={`Descripción para nivel ${l.score}…`}
                  data-testid={`kpi-editor-level-${l.score}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 mt-4">
          <Field label="Peso (0–10)">
            <input type="number" min="0" max="10" step="0.1" value={model.weight ?? 1} onChange={(e) => setField("weight", e.target.value)} className="input-field" />
          </Field>
          <Field label="Orden">
            <input type="number" min="0" value={model.order ?? 0} onChange={(e) => setField("order", e.target.value)} className="input-field" />
          </Field>
          <Field label="Estado">
            <button type="button" onClick={() => setField("active", !model.active)} className={`sketch-pill px-3 py-2 text-sm font-semibold ${model.active ? "bg-[var(--lala-green,#2A8A3E)] text-white" : "bg-[var(--cream-deep)] text-[var(--ink-soft)]"}`}>
              {model.active ? "Activo" : "Inactivo"}
            </button>
          </Field>
          <Field label="Origen">
            <button type="button" onClick={() => setField("is_proposed", !model.is_proposed)} className={`sketch-pill px-3 py-2 text-sm font-semibold inline-flex items-center gap-1 ${model.is_proposed ? "bg-[#FFC400] text-[var(--ink)]" : "bg-[var(--lala-blue)] text-white"}`} data-testid="kpi-editor-proposed-toggle" title="Propuesta = sugerencia del equipo LALA Elite pendiente de validación oficial.">
              {model.is_proposed ? "Propuesta" : "Oficial Lala"}
            </button>
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="sketch-pill bg-white border border-[var(--ink-soft)] text-[var(--ink)] px-4 py-2 text-sm" data-testid="kpi-editor-cancel">Cancelar</button>
          <button onClick={() => onSave({ ...model, levels })} disabled={busy} className="btn-primary text-sm" data-testid="kpi-editor-save">
            <Save size={14} /> {busy ? "Guardando…" : "Guardar indicador"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}

function ExcelImportButton({ onAfterImport, setMsg, setErr }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState(`${new Date().getFullYear()}`);
  const [showPicker, setShowPicker] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = () => {
    if (!period.trim()) {
      setErr("Define el periodo antes de cargar el Excel.");
      return;
    }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setMsg(""); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("period", period);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/kpi-catalog/import-excel`,
        { method: "POST", body: fd, credentials: "include" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error de importación");
      setResult(data);
      setMsg(`Importación lista · ${data.inserted} nuevas · ${data.updated} actualizadas · periodo ${data.period}.`);
      setTimeout(() => setMsg(""), 5000);
      onAfterImport && onAfterImport();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="inline-flex flex-col gap-1" data-testid="excel-import">
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setShowPicker((p) => !p)}
          disabled={busy}
          className="sketch-pill bg-[#FFC400] text-[var(--ink)] px-3 py-2 text-sm font-semibold inline-flex items-center gap-2"
          data-testid="excel-import-btn"
        >
          <Upload size={14} /> {busy ? "Importando…" : "Importar Excel LALA"}
        </button>
      </div>
      {showPicker && (
        <div className="bg-white border border-[var(--line)] rounded-xl p-3 shadow-md mt-1 w-72" data-testid="excel-import-panel">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder="2026, 2026-Q1, etc."
            className="input-field text-sm"
            data-testid="excel-period-input"
          />
          <button onClick={onPick} disabled={busy} className="btn-primary mt-2 w-full text-sm justify-center" data-testid="excel-pick-file">
            <Upload size={14} /> Elegir archivo .xlsx
          </button>
          <p className="text-[10px] text-[var(--ink-soft)] mt-2 leading-relaxed">
            Acepta el formato oficial LALA con columnas <b>Pilar · Criterio · Indicador</b> y bloques
            <b> Proveedor 1..N</b> con pares Real/Objetivo (1-4). Si el Excel no tiene datos capturados,
            el importador igual los reconoce y reporta el mapeo.
          </p>
          {result && (
            <div className="mt-2 text-[10px] bg-[var(--cream-soft)] border border-[var(--line)] rounded p-2 max-h-40 overflow-y-auto" data-testid="excel-import-result">
              <div className="font-bold">Resultado del último import:</div>
              <div>Insertadas: {result.inserted} · Actualizadas: {result.updated}</div>
              <div>Periodo: {result.period}</div>
              {result.skipped_no_indicator?.length > 0 && (
                <div className="mt-1">⚠ Sin match: {result.skipped_no_indicator.slice(0, 3).join(", ")}{result.skipped_no_indicator.length > 3 ? "…" : ""}</div>
              )}
              {result.supplier_mapping && (
                <div className="mt-1">Mapeo: {Object.entries(result.supplier_mapping).map(([n, name]) => `${n}→${name}`).slice(0, 3).join(", ")}…</div>
              )}
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onFile} style={{ display: "none" }} data-testid="excel-file-input" />
    </div>
  );
}
